import React, { useState, useEffect } from "react";
import { getDatabase, ref, get } from "firebase/database";
import app from "../firebase-config";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Helper function to format dates to 'YYYY-MM-DD'
const formatDate = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const day = d.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

function SalesHistory() {
  const [sales, setSales] = useState([]);
  const [filteredSales, setFilteredSales] = useState([]);
  const [totalSales, setTotalSales] = useState(0);
  const [searchDate, setSearchDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  // Fetch sales history from Firebase
  const fetchSalesHistory = async () => {
    const db = getDatabase(app);
    const salesRef = ref(db, "sales");
    const snapshot = await get(salesRef);
    if (snapshot.exists()) {
      const salesData = snapshot.val();
      const salesArray = Object.keys(salesData).map((key) => ({
        ...salesData[key],
        saleId: key,
      }));
  
      // Sort sales by timestamp in descending order (latest first)
      salesArray.sort((a, b) => {
        const aTimestamp = new Date(a.timestamp);
        const bTimestamp = new Date(b.timestamp);
        return bTimestamp - aTimestamp;
      });
  
      setSales(salesArray);
      setFilteredSales(salesArray);
      updateTotalSalesForToday(salesArray);
    } else {
      console.log("No sales found in database.");
    }
  };

  const handleDateSearch = (event) => {
    const selectedDate = event.target.value;
    setSearchDate(selectedDate);

    if (selectedDate) {
      const filtered = sales.filter((sale) => {
        const saleDateFormatted = formatDate(sale.timestamp);
        return saleDateFormatted === selectedDate;
      });
      setFilteredSales(filtered);
      updateTotalSales(filtered);
    } else {
      setFilteredSales(sales);
      updateTotalSalesForToday(sales);
    }
    setCurrentPage(1);
  };

  const updateTotalSales = (salesList) => {
    const total = salesList.reduce((acc, sale) => acc + sale.totalAmount, 0);
    setTotalSales(total);
  };

  const updateTotalSalesForToday = (salesList) => {
    const todayDate = formatDate(new Date());
    const todaySales = salesList.filter((sale) => {
      const saleDateFormatted = formatDate(sale.timestamp);
      return saleDateFormatted === todayDate;
    });
    updateTotalSales(todaySales);
  };

  const paginateSales = (salesList) => {
    const indexOfLastSale = currentPage * itemsPerPage;
    const indexOfFirstSale = indexOfLastSale - itemsPerPage;
    return salesList.slice(indexOfFirstSale, indexOfLastSale);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  useEffect(() => {
    fetchSalesHistory();
    setSearchDate(formatDate(new Date()));
  }, []);

  const salesToDisplay = paginateSales(filteredSales);
  const totalPages = Math.ceil(filteredSales.length / itemsPerPage);

  const generatePDF = (filterType) => {
    const doc = new jsPDF();
    doc.text("Sales Report", 20, 10);
  
    let filteredSalesList = sales;
    const todayDate = formatDate(new Date());
  
    if (filterType === "today") {
      filteredSalesList = sales.filter(
        (sale) => formatDate(sale.timestamp) === todayDate
      );
    } else if (filterType === "month") {
      const currentMonth = new Date().getMonth();
      filteredSalesList = sales.filter(
        (sale) => new Date(sale.timestamp).getMonth() === currentMonth
      );
    } else if (filterType === "year") {
      const currentYear = new Date().getFullYear();
      filteredSalesList = sales.filter(
        (sale) => new Date(sale.timestamp).getFullYear() === currentYear
      );
    }
  
    const totalSalesAmount = filteredSalesList.reduce((acc, sale) => acc + Number(sale.totalAmount), 0);
  
    const tableData = filteredSalesList.flatMap((sale) =>
      sale.items.map((item) => {
        const itemPrice = Number(item.price); // Convert price to a number
        const totalItemPrice = itemPrice * Number(item.quantity); // Convert quantity to number and calculate total
  
        return [
          new Date(sale.timestamp).toLocaleString(),
          sale.user,
          item.coffeeName,  // Coffee name
          item.size,        // Size
          item.quantity,    // Quantity
          `Php ${totalItemPrice.toFixed(2)}`, // Total price format
        ];
      })
    );
  
    const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY : 30;
  
    autoTable(doc, {
      head: [["Date", "Cashier", "Item", "Size", "Qty", "Total"]],
      body: tableData,
      startY: finalY,
    });
  
    const finalTableY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : finalY + 10;
    doc.text(`Total Sales: Php ${totalSalesAmount.toFixed(2)}`, 20, finalTableY);
  
    doc.save(`Sales_Report_${todayDate}.pdf`);
  };
  
  
  
  

  return (
    <div className="container mt-5">
      <h2 className="mb-4 text-center">Sales History</h2>

      <div className="d-flex justify-content-center mt-3">
        <button className="btn btn-primary mx-2" onClick={() => generatePDF("today")}>Export Today's Sales</button>
        <button className="btn btn-primary mx-2" onClick={() => generatePDF("month")}>Export Monthly Sales</button>
        <button className="btn btn-primary mx-2" onClick={() => generatePDF("year")}>Export Yearly Sales</button>
      </div>
      <div className="mb-4">
        <input type="date" className="form-control" value={searchDate} onChange={handleDateSearch} />
      </div>

      <div className="mb-4">
        <h4>Total Sales: ₱{totalSales.toFixed(2)}</h4>
      </div>

      <div className="row">
        {salesToDisplay.length > 0 ? (
          salesToDisplay.map((sale) => (
            <div className="col-md-4 mb-4" key={sale.saleId}>
              <div className="card shadow p-3">
                <div className="card-body">
                  <p><strong>Date:</strong> {new Date(sale.timestamp).toLocaleString()}</p>
                  <p><strong>Cashier:</strong> {sale.user}</p>
                  <p><strong>Total Amount:</strong> ₱{sale.totalAmount}</p>
                  <h6>Items:</h6>
                  <ul>
                    {sale.items.map((item, index) => (
                      <li key={index}>{item.coffeeName} {item.size} (x{item.quantity}) - ₱{item.price}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))
        ) : (
          <p>No sales found for the selected date.</p>
        )}
      </div>

      {totalPages > 1 && (
        <div className="d-flex justify-content-center mb-3">
          <span className="mx-2">Page {currentPage} of {totalPages}</span>
          <button className="btn btn-secondary mx-2" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>Previous</button>
          <button className="btn btn-secondary mx-2" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>Next</button>
        </div>
      )}
    </div>
  );
}

export default SalesHistory;